import type { AnyAction } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createGenericSlice from '@/store/reducers/sliceTemplate';
import { deleteData } from '@/core/utils/deleteData';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import type { WidgetDomainsListState } from '@/store/types/widgetDomainsListTypes';
import { parseWidgetDomainsResponse } from '@/store/utils/parseWidgetDomainsResponse';

const domainsListSlice = createGenericSlice<string>('domainsList');

const genericInitial = domainsListSlice.getInitialState();

const initialState: WidgetDomainsListState = {
	...genericInitial,
	maxAllowedDomains: null,
};

const combinedSlice = createSlice({
	name: 'domainsList',
	initialState,
	reducers: {
		...domainsListSlice.caseReducers,
		setDomainsListNormalized(
			state,
			action: PayloadAction<{ domains: string[]; maxAllowedDomains: number | null }>,
		) {
			state.data = action.payload.domains;
			state.maxAllowedDomains = action.payload.maxAllowedDomains;
			state.isFetching = false;
			state.hasFetchError = false;
		},
	},
});

export const {
	fetchDataStart,
	fetchDataError,
	setDataLoaded,
	deleteDataSuccess,
	resetError,
	setDomainsListNormalized,
} = combinedSlice.actions;

export const fetchDomainsListData =
	() => async (dispatch: Dispatch<AnyAction>) => {
		dispatch(combinedSlice.actions.fetchDataStart());
		try {
			const response = await apiUrl.get(API_ENDPOINTS.getDomainList);
			const { domains, maxAllowed } = parseWidgetDomainsResponse(response.data);
			dispatch(
				combinedSlice.actions.setDomainsListNormalized({
					domains,
					maxAllowedDomains: maxAllowed,
				}),
			);
		} catch (error) {
			console.error('Error during domains list fetch:', error);
			handleResponseError(error, (errorMessage, isNetworkError) => {
				dispatch(
					combinedSlice.actions.fetchDataError({ errorMessage, isNetworkError }),
				);
			});
		}
	};

export const deleteDomainData = (item: string) => deleteData(combinedSlice, API_ENDPOINTS.deleteDomain)(item);

export default combinedSlice.reducer;
export { combinedSlice as domainsListSlice };
